import React, { useEffect, useRef } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flip } from 'gsap/Flip';

gsap.registerPlugin(ScrollTrigger, Flip);

const Hero = () => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const imageContainerRef = useRef(null);
  const headingRef = useRef(null);
  const accentRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const words = headingRef.current.querySelectorAll('.hero-word');

      gsap.set(words, { yPercent: 120, opacity: 0, rotationZ: 6 });

      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      tl.to(words, {
        yPercent: 0,
        opacity: 1,
        rotationZ: 0,
        duration: 1.1,
        stagger: 0.08,
      })
        .fromTo(
          contentRef.current.querySelector('.hero-sub'),
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8 },
          '-=0.5'
        )
        .fromTo(
          contentRef.current.querySelector('.hero-cta'),
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6 },
          '-=0.5'
        );

      gsap.fromTo(
        imageContainerRef.current,
        { opacity: 0, x: 100 },
        { opacity: 1, x: 0, duration: 1, delay: 0.2, ease: 'power3.out' }
      );

      gsap.to(accentRef.current, {
        backgroundPositionX: '200%',
        duration: 6,
        ease: 'none',
        repeat: -1,
      });

      gsap.to(headingRef.current, {
        yPercent: -20,
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });

      gsap.to(words, {
        letterSpacing: '0.05em',
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  const renderWords = (text, extraClass = '') =>
    text.split(' ').map((word, i) => (
      <span key={`${word}-${i}`} className="inline-block overflow-hidden align-bottom pr-[0.25em]">
        <span className={`hero-word inline-block will-change-transform ${extraClass}`}>{word}</span>
      </span>
    ));

  return (
    <div ref={containerRef} className="relative overflow-hidden bg-white px-4 pb-6 pt-4 sm:px-6 sm:pb-8 sm:pt-6 lg:px-8 lg:pb-12 lg:pt-8">
      <div className="relative min-h-auto sm:min-h-[500px] lg:min-h-[70vh] flex flex-col lg:flex-row items-center lg:items-start overflow-hidden gap-8 lg:gap-0">
          
          {/* Left Content */}
          <div ref={contentRef} className="w-full lg:w-1/2 z-20 flex flex-col justify-center text-center lg:text-left">
            <div className="max-w-3xl mx-auto lg:mx-0">
              <h1
                ref={headingRef}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-[4rem] xl:text-[4.75rem] font-bold leading-[1.1] mb-6 tracking-tight text-gray-900"
              >
                <span className="block">{renderWords('Elevate Your Look')}</span>
                <span className="block">{renderWords('Into Something')}</span>
                <span
                  ref={accentRef}
                  className="block bg-gradient-to-r from-[#F05A35] via-[#ff8a5c] to-[#F05A35] bg-[length:200%_100%] bg-clip-text text-transparent"
                >
                  {renderWords('People Remember')}
                </span>
              </h1>

              <p className="hero-sub text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                Experience premium salon services with our expert team. Transform your look and boost your confidence with personalized care and attention to detail.
              </p>

              <div className="hero-cta flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4">
                <Link 
                  to="/bookings"
                  className="inline-flex items-center gap-2 bg-[#F05A35] text-white px-5 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base hover:bg-[#d94a28] transition-colors"
                >
                  Book Appointment <ArrowRightIcon className="w-4 h-4" />
                </Link>
                <button className="px-5 sm:px-6 py-2 sm:py-3 rounded-full border-2 border-gray-300 font-semibold text-sm sm:text-base text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors">
                  Explore Services
                </button>
              </div>
            </div>
          </div>

          {/* Right Image Container - Full Height, 50% Width */}
          <div 
            ref={imageContainerRef}
            className="w-full lg:absolute lg:top-0 lg:right-0 lg:h-full lg:w-1/2 z-10 h-64 sm:h-80 md:h-96 lg:h-full"
            style={{
              perspective: '1500px',
              overflow: 'hidden'
            }}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Straight, borderless video */}
              <video
                src="/videos/hero-booking.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-contain"
              />

              {/* Background depth circles */}
              <div 
                className="absolute w-96 h-96 rounded-full bg-[#F05A35]/5 blur-3xl"
                style={{
                  right: '-200px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
              ></div>
              
              <div 
                className="absolute w-72 h-72 rounded-full bg-[#6EC1E4]/5 blur-3xl"
                style={{
                  right: '150px',
                  top: '20%',
                  pointerEvents: 'none'
                }}
              ></div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default Hero;
